import React, { useEffect, useRef, useState } from 'react';
import { 
  View, 
  KeyboardAvoidingView, 
  Platform, 
  Dimensions, 
  ViewStyle,
  SafeAreaView 
} from 'react-native';
import { Theme } from '../../utils/theme';

interface SafariOptimizedContainerProps {
  children: React.ReactNode;
  style?: ViewStyle;
  keyboardOffset?: number;
  avoidKeyboard?: boolean;
}

/**
 * A container component optimized for Safari that prevents race conditions
 * and layout issues during keyboard interactions and content changes.
 */
export const SafariOptimizedContainer: React.FC<SafariOptimizedContainerProps> = ({
  children,
  style,
  keyboardOffset = 0,
  avoidKeyboard = true,
}) => {
  const [screenHeight, setScreenHeight] = useState(Dimensions.get('window').height);
  const [isKeyboardVisible, setIsKeyboardVisible] = useState(false);
  const layoutTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const subscription = Dimensions.addEventListener('change', ({ window }) => {
      // Clear any pending layout updates
      if (layoutTimeoutRef.current) {
        clearTimeout(layoutTimeoutRef.current);
      }

      // Debounce screen height updates to prevent layout thrashing
      layoutTimeoutRef.current = setTimeout(() => {
        const newHeight = window.height;
        const heightDiff = Math.abs(newHeight - screenHeight);
        
        // Only update if height change is significant (likely keyboard)
        if (heightDiff > 100) {
          setScreenHeight(newHeight);
          setIsKeyboardVisible(newHeight < screenHeight);
        }
      }, 100);
    });

    return () => {
      subscription?.remove();
      if (layoutTimeoutRef.current) {
        clearTimeout(layoutTimeoutRef.current);
      }
    };
  }, [screenHeight]);

  // Use different strategies based on platform and keyboard state
  const getContainerBehavior = () => {
    if (Platform.OS === 'web') {
      // On web (Safari), use 'height' behavior for more stable layout
      return 'height';
    }
    return Platform.OS === 'ios' ? 'padding' : 'height';
  };

  const containerStyle = [
    {
      flex: 1,
      backgroundColor: Theme.colors.background,
    },
    style,
  ];

  // For web/Safari, use a more stable approach
  if (Platform.OS === 'web') {
    return (
      <SafeAreaView style={containerStyle}>
        <View style={{ flex: 1 }}>
          {avoidKeyboard ? (
            <KeyboardAvoidingView
              style={{ flex: 1 }}
              behavior={getContainerBehavior()}
              keyboardVerticalOffset={keyboardOffset}
              enabled={Platform.OS !== 'web'} // Disable on web to prevent issues
            >
              <View style={{ flex: 1 }}>
                {children}
              </View>
            </KeyboardAvoidingView>
          ) : (
            <View style={{ flex: 1 }}>
              {children}
            </View>
          )}
        </View>
      </SafeAreaView>
    );
  }

  // For native platforms, use the traditional approach
  return (
    <SafeAreaView style={containerStyle}>
      {avoidKeyboard ? (
        <KeyboardAvoidingView
          style={{ flex: 1 }}
          behavior={getContainerBehavior()}
          keyboardVerticalOffset={keyboardOffset}
        >
          {children}
        </KeyboardAvoidingView>
      ) : (
        <View style={{ flex: 1 }}>
          {children}
        </View>
      )}
    </SafeAreaView>
  );
};

export default SafariOptimizedContainer; 